import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Send, AlertCircle, Fuel, Clock, Gauge, Activity, Zap, Calendar, Factory } from "lucide-react";
import { api, formatError, CALENDLY } from "@/lib/api";
import { openExternal } from "@/lib/openExternal";
import { INDUSTRY_OPTIONS, COUNTRIES } from "@/lib/data";

export default function AssessmentForm() {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    country: "", industry: "", project_location: "",
    generator_capacity: "", daily_running_hours: "", diesel_consumption: "",
    load_profile: "", electricity_demand: "", project_duration: "",
    site_visit: true, message: "",
  });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading"); setErr("");
    try {
      await api.post("/leads", { ...form, source: "assessment" });
      setStatus("success");
    } catch (ex) {
      setStatus("error");
      setErr(formatError(ex));
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="panel p-10 text-center"
        data-testid="assessment-success"
      >
        <div className="w-12 h-12 mx-auto border border-tratou-green/60 flex items-center justify-center mb-5">
          <Check className="w-6 h-6 text-tratou-green" />
        </div>
        <h3 className="font-display text-2xl font-bold mb-2">Assessment request received.</h3>
        <p className="text-tratou-dim max-w-md mx-auto">
          A Tratou engineer will review your diesel-generator profile and reach out within 1 business
          day to confirm a complimentary site assessment.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => openExternal(e, CALENDLY)}
            className="btn-primary"
            data-testid="assessment-book-now"
          >
            <Calendar className="w-4 h-4" /> Book Now via Calendly
          </a>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="panel p-6 md:p-10" data-testid="assessment-form">
      {/* Section 1: Contact */}
      <SectionHeader icon={Zap} eyebrow="01 · Contact" title="Who are we talking to?" />
      <div className="grid gap-5 md:grid-cols-2 mt-6">
        <Field label="Full Name *" value={form.name} onChange={update("name")} required testid="af-name" />
        <Field label="Company" value={form.company} onChange={update("company")} testid="af-company" />
        <Field label="Work Email *" type="email" value={form.email} onChange={update("email")} required testid="af-email" />
        <Field label="Phone" value={form.phone} onChange={update("phone")} testid="af-phone" />
        <Select label="Country" value={form.country} onChange={update("country")} options={COUNTRIES} testid="af-country" />
        <Select label="Industry" value={form.industry} onChange={update("industry")} options={INDUSTRY_OPTIONS} testid="af-industry" />
      </div>

      {/* Section 2: Diesel profile */}
      <SectionHeader icon={Fuel} eyebrow="02 · Diesel Generator Profile" title="Tell us about your current setup." className="mt-12" />
      <div className="grid gap-5 md:grid-cols-2 mt-6">
        <Field icon={Factory} label="Generator Capacity *" placeholder="e.g. 250 kVA / 200 kW" value={form.generator_capacity} onChange={update("generator_capacity")} required testid="af-gen-cap" />
        <Field icon={Clock} label="Daily Running Hours *" placeholder="e.g. 16 hrs/day" value={form.daily_running_hours} onChange={update("daily_running_hours")} required testid="af-hours" />
        <Field icon={Fuel} label="Diesel Consumption" placeholder="e.g. 350 L/day" value={form.diesel_consumption} onChange={update("diesel_consumption")} testid="af-fuel" />
        <Field icon={Activity} label="Electricity Demand" placeholder="e.g. 1,800 kWh/day" value={form.electricity_demand} onChange={update("electricity_demand")} testid="af-demand" />
      </div>
      <div className="mt-5">
        <label className="field-label flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-tratou-green" /> Load Profile</label>
        <textarea
          value={form.load_profile}
          onChange={update("load_profile")}
          rows={2}
          placeholder="e.g. Base 80 kW continuous · peaks to 180 kW during welding · idle at night"
          className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm"
          data-testid="af-load"
        />
      </div>

      {/* Section 3: Project */}
      <SectionHeader icon={Calendar} eyebrow="03 · Project Context" title="Where and when?" className="mt-12" />
      <div className="grid gap-5 md:grid-cols-2 mt-6">
        <Field label="Project / Site Location" placeholder="City, region" value={form.project_location} onChange={update("project_location")} testid="af-location" />
        <Select label="Project Duration" value={form.project_duration} onChange={update("project_duration")} options={["< 1 month", "1 – 3 months", "3 – 6 months", "6 – 12 months", "1 – 2 years", "Permanent"]} testid="af-duration" />
      </div>
      <div className="mt-5">
        <label className="field-label">Anything else?</label>
        <textarea
          value={form.message}
          onChange={update("message")}
          rows={3}
          placeholder="Add any constraints, timing, or questions..."
          className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm"
          data-testid="af-message"
        />
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer group" data-testid="af-site-visit-wrap">
        <input
          type="checkbox"
          checked={form.site_visit}
          onChange={(e) => setForm((f) => ({ ...f, site_visit: e.target.checked }))}
          className="mt-0.5 w-4 h-4 accent-tratou-green cursor-pointer"
          data-testid="af-site-visit"
        />
        <span className="text-sm text-tratou-dim group-hover:text-white transition">
          Yes, send a Tratou engineer to our site for a full in-person diesel-to-MBESS assessment.
        </span>
      </label>

      {err && (
        <div className="mt-5 flex items-start gap-2 text-red-400 text-sm" data-testid="assessment-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{err}</span>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <button
          type="submit"
          disabled={status === "loading"}
          className="btn-primary disabled:opacity-50"
          data-testid="assessment-submit"
        >
          {status === "loading" ? "Transmitting..." : "Get My Free Assessment"}
          <Send className="w-4 h-4" />
        </button>
        <a
          href={CALENDLY}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => openExternal(e, CALENDLY)}
          className="btn-secondary justify-center"
          data-testid="assessment-calendly"
        >
          <Calendar className="w-4 h-4 text-tratou-green" /> Prefer to book directly
        </a>
      </div>
      <p className="mt-4 text-xs text-tratou-faint">
        Savings and suitability depend on site load, charging source, utilisation and operating conditions. Zero cost · zero obligation.
      </p>
    </form>
  );
}

function SectionHeader({ icon: Icon, eyebrow, title, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-tratou-green" />
        <span className="eyebrow">{eyebrow}</span>
      </div>
      <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">{title}</h3>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", required = false, icon: Icon, testid }) {
  return (
    <div>
      <label className="field-label flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-tratou-green" />}
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <select
        value={value}
        onChange={onChange}
        className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm"
        data-testid={testid}
      >
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
